import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/api';
import Modal from '../../components/Modal';
import { editBoundary, money } from '../../domain/bpointFlow.browser';

const amount = (value) => Number(value).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dateLabel = (date) => date.split('-').reverse().join('/');
const DESCRIPTIONS = {
  1415: 'Prospetto informativo disabili',
  458: 'Stampe per bilancio (IRAP - ISA - ratei)',
  1422: 'Trasmissione telematica Autoliquidazione Inail',
  1420: 'Trasmissione telematica 770 subordinati',
  427: 'Fasce sulla media ordinaria trimestrale',
  447: 'Canone aggiornamento anagraf. software paghe lavoratori aderenti assistenza sanitaria integrativa',
  456: 'Imputazione delle presenze mensili',
  461: 'Canone per la pubblicazione documentale HR Portal',
  462: 'Canone per la pubblicazione documentale dipendenti HR Portal',
};
const NOTES = {
  1415: 'Quota base per fascia di dipendenti + quota per provincia, con tetto massimo sul totale. Voce disponibile nelle sezioni 3 e 6.',
  447: 'Applicata ai clienti con la spunta “Fondi Assicurazione sanitaria”. Tariffa sulla media dei dipendenti × mesi del trimestre con almeno un cedolino.',
  456: 'Paghe Infinity usa la colonna Infoday; Manuale/Manuali usa la colonna Manuale. Tariffa sulla media dei mesi attivi × mesi con almeno un cedolino.',
  461: 'Applicata con la spunta “Pubblicazione documenti azienda”. Tariffa sulla media dei dipendenti dei mesi ordinari × mesi con almeno un cedolino.',
  462: 'Applicata con la spunta “Pubblicazione documenti ai dipendenti”. Tariffa sulla media dei tre mesi ordinari × cedolini trimestrali, incluse le mensilità supplementari.',
};

function FasceCu1421({ date, canEdit }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState([]);
  const [busy, setBusy] = useState(false);
  const [historyFocus, setHistoryFocus] = useState(null);
  const load = useCallback(async () => {
    try {
      const response = await api.get('/billing/bpoint-listino/1421/fasce', { params: { data_decorrenza: date } });
      setData(response.data); setDraft(response.data.fasce || []);
    } catch (error) { toast.error(error.response?.data?.message || 'Impossibile caricare le fasce CU 1421'); }
  }, [date]);
  useEffect(() => { load(); }, [load]);
  const normalize = (rows) => rows.map((row) => ({
    min_quantita: Number(row.min_quantita), max_quantita: row.max_quantita == null ? null : Number(row.max_quantita),
    diritto_fisso: Number(row.diritto_fisso), importo_unitario: Number(row.importo_unitario),
  }));
  const change = (index, field, value) => setDraft((rows) => rows.map((row, current) => {
    if (current === index) return { ...row, [field]: value };
    if (field === 'max_quantita' && current === index + 1 && value !== '') return { ...row, min_quantita: Number(value) + 1 };
    if (field === 'min_quantita' && current === index - 1 && value !== '') return { ...row, max_quantita: Number(value) - 1 };
    return row;
  }));
  const save = async () => {
    if (!data || JSON.stringify(normalize(draft)) === JSON.stringify(normalize(data.fasce || []))) return;
    setBusy(true);
    try {
      await api.put('/billing/bpoint-listino/1421/fasce', { data_decorrenza: date, fasce: normalize(draft) });
      toast.success(`Fasce CU 1421 salvate dal ${dateLabel(date)}`); await load();
    } catch (error) { toast.error(error.response?.data?.message || 'Salvataggio delle fasce non riuscito'); await load(); }
    finally { setBusy(false); }
  };
  // Lo storico della 1421 e' restituito per tutte le decorrenze. I pulsanti
  // della tabella corrente devono pero' riflettere soltanto le variazioni
  // della versione visualizzata: una modifica di settembre non deve colorare
  // le stesse posizioni nella versione di ottobre.
  const storicoCompleto = data?.storico || [];
  const storicoCorrente = storicoCompleto.filter((snapshot) =>
    snapshot.data_decorrenza === data?.data_decorrenza);
  // La prima modifica di una nuova decorrenza deve confrontarsi con il valore
  // effettivo precedente. Senza questa baseline lo storico esiste nel DB, ma il
  // pulsante resta grigio finché non viene eseguita una seconda modifica.
  const baseline = storicoCorrente.length ? [...storicoCompleto]
    .filter((snapshot) => snapshot.data_decorrenza < data?.data_decorrenza)
    .sort((left, right) => right.data_decorrenza.localeCompare(left.data_decorrenza)
      || new Date(right.recorded_at || 0) - new Date(left.recorded_at || 0))[0] : null;
  const storico = baseline ? [...storicoCorrente, { ...baseline, baseline: true, deletable: false }] : storicoCorrente;
  const rangeLabel = (row) => `${row.min_quantita}–${row.max_quantita ?? '∞'}`;
  const historyValue = (snapshot, index, field) => {
    const row = snapshot.fasce?.[index];
    if (!row) return null;
    // Modificare un confine cambia sia il massimo della fascia corrente sia il
    // minimo della successiva: entrambe mantengono la propria identità e devono
    // risultare modificate rispetto alla baseline.
    return field === 'fasce' ? [Number(row.min_quantita), row.max_quantita == null ? null : Number(row.max_quantita)] : Number(row[field]);
  };
  const historyEntries = (index, field) => {
    let previous;
    const entries = [];
    [...storico].reverse().forEach((snapshot) => {
      const value = historyValue(snapshot, index, field);
      const serialized = JSON.stringify(value);
      if (value != null && serialized !== previous) entries.push({ ...snapshot, initial: entries.length === 0 });
      previous = serialized;
    });
    return entries.reverse();
  };
  const hasHistory = (index, field) => {
    const entries = historyEntries(index, field);
    if (entries.length < 2) return false;
    const baselineValue = historyValue(entries[entries.length - 1], index, field);
    const currentValue = historyValue({ fasce: data?.fasce || [] }, index, field);
    return JSON.stringify(currentValue) !== JSON.stringify(baselineValue);
  };
  const removeHistory = async (entry) => {
    if (!entry.deletable || busy || !canEdit) return;
    if (!window.confirm('Eliminare questa modifica? Saranno ripristinati i valori precedenti salvati nella stessa operazione.')) return;
    setBusy(true);
    try {
      await api.delete(`/billing/bpoint-listino/1421/operazioni/${encodeURIComponent(entry.operazione_id)}`);
      toast.success('Modifica eliminata'); setHistoryFocus(null); await load();
    } catch (error) { toast.error(error.response?.data?.message || 'Eliminazione della modifica non riuscita'); await load(); }
    finally { setBusy(false); }
  };
  const historyButton = (index, field, label, buttonLabel) => {
    const enabled = hasHistory(index, field);
    return <button type="button" disabled={!enabled || busy}
      title={`Storico ${label} fascia ${index + 1}`} aria-label={`Storico ${label} fascia ${index + 1}`}
      onClick={() => setHistoryFocus({ field, label, index })}
      className={`h-8 rounded border px-2 text-xs font-semibold ${enabled ? 'border-purple-600 bg-purple-600 text-white' : 'border-gray-200 bg-gray-50 text-gray-300'}`}>
      {buttonLabel}
    </button>;
  };
  return <fieldset disabled={busy} className="rounded-lg border bg-gray-50 p-4">
    <div className="mb-1 font-semibold">1421 · Trasmissione telematica CU subordinati</div>
    <p className="mb-2 text-xs text-gray-600">Diritto fisso più importo per ogni CU. Le modifiche vengono salvate automaticamente; l'ultima fascia termina a ∞.</p>
    {!data ? <div className="text-sm">Caricamento fasce CU…</div> : <div className="max-w-5xl overflow-auto rounded border border-purple-200 bg-white"><table className="w-full text-sm"><thead className="bg-purple-50 text-purple-950"><tr><th className="p-2 text-left">Da CU</th><th className="p-2 text-left">A CU</th><th className="p-2 text-left">Diritto fisso €</th><th className="p-2 text-left">€ per CU</th></tr></thead><tbody>{draft.map((row, index) => <tr key={`${index}-${row.min_quantita}`} className="border-t"><td className="p-1"><input aria-label={`1421 fascia ${index + 1} da CU`} type="number" min="1" step="1" disabled={!canEdit || index === 0} value={row.min_quantita ?? ''} onChange={(event) => change(index, 'min_quantita', event.target.value)} onBlur={save} className="h-8 w-24 rounded border border-gray-200 px-1 text-right disabled:bg-gray-50" /></td><td className="p-1"><div className="flex items-center gap-1">{index === draft.length - 1 ? <div className="flex h-8 w-24 items-center justify-end rounded border border-gray-200 bg-gray-50 px-1 text-gray-500">∞</div> : <input aria-label={`1421 fascia ${index + 1} a CU`} type="number" min="1" step="1" disabled={!canEdit} value={row.max_quantita ?? ''} onChange={(event) => change(index, 'max_quantita', event.target.value)} onBlur={save} className="h-8 w-24 rounded border border-gray-200 px-1 text-right disabled:bg-gray-50" />}{historyButton(index, 'fasce', 'fasce', 'Fasce')}</div></td><td className="p-1"><div className="flex items-center gap-1"><input aria-label={`1421 fascia ${index + 1} diritto_fisso`} type="number" min="0" step="0.01" disabled={!canEdit} value={row.diritto_fisso ?? ''} onChange={(event) => change(index, 'diritto_fisso', event.target.value)} onBlur={save} className="h-8 w-24 rounded border border-gray-200 px-1 text-right disabled:bg-gray-50" />{historyButton(index, 'diritto_fisso', 'diritto fisso', 'Importi')}</div></td><td className="p-1"><div className="flex items-center gap-1"><input aria-label={`1421 fascia ${index + 1} importo_unitario`} type="number" min="0" step="0.01" disabled={!canEdit} value={row.importo_unitario ?? ''} onChange={(event) => change(index, 'importo_unitario', event.target.value)} onBlur={save} className="h-8 w-24 rounded border border-gray-200 px-1 text-right disabled:bg-gray-50" />{historyButton(index, 'importo_unitario', '€ per CU', 'Importi')}</div></td></tr>)}</tbody></table></div>}
    {historyFocus && <Modal isOpen size="tiny" closeButton={() => setHistoryFocus(null)} headerText={`Storico · 1421 · ${historyFocus.label.replace(/^Storico /, '')}`}>
      <div className="max-h-[66vh] space-y-2 overflow-auto">{historyEntries(historyFocus.index, historyFocus.field).map((snapshot) => <div key={snapshot.operazione_id || snapshot.data_decorrenza} className="rounded border border-purple-200 p-2">
        <div className="mb-2 flex items-center justify-between gap-2"><span className="font-semibold text-purple-900">{dateLabel(snapshot.data_decorrenza)}</span><span className="text-xs text-gray-500">{snapshot.initial ? 'Valore iniziale' : snapshot.updated_by ? `da ${snapshot.updated_by}` : 'Storico precedente · autore non verificabile'}</span>{canEdit && snapshot.deletable && !snapshot.initial && <button type="button" disabled={busy} className="rounded border border-red-200 px-2 py-1 text-xs text-red-700" onClick={() => removeHistory(snapshot)}>Elimina</button>}</div>
        {snapshot.recorded_at && <div className="mb-1 text-xs text-gray-500">Registrata il {new Date(snapshot.recorded_at).toLocaleString('it-IT', { timeZone: 'Europe/Rome' })}</div>}
        <div className="rounded bg-purple-50 px-2 py-1 text-right font-semibold text-purple-950">{historyFocus.field === 'fasce'
          ? rangeLabel(snapshot.fasce[historyFocus.index])
          : `${amount(snapshot.fasce[historyFocus.index][historyFocus.field])} €`}</div>
      </div>)}</div>
    </Modal>}
  </fieldset>;
}

export default function BpointListinoEditor({ date, canEdit }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const draftRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [focus, setFocus] = useState(null);
  const dateRef = useRef(date);
  dateRef.current = date;
  const loadSequence = useRef(0);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    const requestedDate = dateRef.current;
    setLoading(true);
    try {
      const response = await api.get('/billing/bpoint-listino', { params: { data_decorrenza: requestedDate } });
      if (mounted.current && dateRef.current === requestedDate && loadSequence.current === sequence) setData(response.data);
    } catch (cause) {
      if (mounted.current && dateRef.current === requestedDate) setError(cause.response?.data?.message || 'Caricamento non riuscito. Riprova.');
    } finally {
      if (mounted.current && dateRef.current === requestedDate && loadSequence.current === sequence) setLoading(false);
    }
  }, []);
  useEffect(() => {
    draftRef.current = null; setDraft(null); setFocus(null); setData(null); setError(''); load();
  }, [date, load]);

  const begin = (group, row, field) => {
    if (busyRef.current || !canEdit) return;
    const editing = { group, bandId: row.id, field, value: String(row[field] ?? ''), rows: group.rows, error: '' };
    draftRef.current = editing; setDraft(editing);
  };
  const change = (group, row, field, value) => {
    let editing = draftRef.current;
    if (!editing || editing.bandId !== row.id || editing.field !== field) {
      begin(group, row, field); editing = draftRef.current;
    }
    if (!editing) return;
    let rows = editing.group.rows;
    let message = '';
    try {
      rows = field === 'price'
        ? rows.map((item) => item.id === row.id ? { ...item, price: money(value) } : item)
        : editBoundary(rows, row.id, field, value, group);
    } catch (cause) { message = cause.message; }
    const next = { ...editing, value, rows, error: message };
    draftRef.current = next; setDraft(next); setError('');
  };
  const cancel = () => { draftRef.current = null; setDraft(null); };
  const save = async () => {
    const editing = draftRef.current;
    if (!editing || busyRef.current) return;
    const original = editing.group.rows.find((row) => row.id === editing.bandId);
    if (editing.value !== '' && Number(editing.value) === original[editing.field]) { cancel(); return; }
    if (editing.error || editing.value === '') {
      setError(editing.error || 'Il valore non può essere vuoto'); cancel(); return;
    }
    busyRef.current = true; setBusy(true); setError(''); cancel();
    try {
      await api.post('/billing/bpoint-listino/operazioni', {
        groupId: editing.group.id, expectedRevision: data.revision, date,
        requestId: window.crypto.randomUUID(),
        operation: editing.field === 'price'
          ? { kind: 'price', bandId: editing.bandId, value: Number(editing.value) }
          : { kind: 'boundary', bandId: editing.bandId, field: editing.field, value: Number(editing.value) },
      });
      if (mounted.current) toast.success(`Voce ${editing.group.code} salvata dal ${dateLabel(date)}`);
    } catch (cause) {
      if (mounted.current) setError(cause.response?.data?.message || 'Salvataggio non confermato. Verifica i valori ricaricati prima di riprovare.');
    } finally {
      if (mounted.current) await load();
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const remove = async (entry) => {
    if (busyRef.current || !entry.deletable || !canEdit) return;
    const detail = entry.coupled ? ' Saranno annullati anche gli aggiustamenti dei confini collegati a questa operazione, non le modifiche separate agli importi.' : '';
    if (!window.confirm(`Eliminare questa modifica con decorrenza ${dateLabel(entry.date)}?${detail}`)) return;
    busyRef.current = true; setBusy(true); setError('');
    try {
      await api.delete(`/billing/bpoint-listino/operazioni/${encodeURIComponent(entry.eventId)}`, {
        data: { groupId: focus.groupId, expectedRevision: data.revision },
      });
      toast.success('Modifica eliminata');
      setFocus(null);
    } catch (cause) { setError(cause.response?.data?.message || 'Eliminazione non confermata: verifica lo storico aggiornato.'); }
    finally { await load(); busyRef.current = false; setBusy(false); }
  };

  const input = (group, row, field, disabled = false) => {
    const editing = draft?.group.id === group.id && draft.bandId === row.id && draft.field === field;
    return <input aria-label={`${group.code} ${group.title} fascia ${row.id} ${field}`} type="number"
      step={field === 'price' ? '0.01' : '1'} disabled={!canEdit || busy || disabled}
      className={`h-8 w-24 rounded border px-1 disabled:bg-gray-50 ${editing ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`}
      value={editing ? draft.value : row[field] ?? ''} placeholder="∞"
      onFocus={() => begin(group, row, field)} onChange={(event) => change(group, row, field, event.target.value)}
      onBlur={save} onKeyDown={(event) => {
        if (event.key === 'Escape') { cancel(); event.currentTarget.blur(); }
        if (event.key === 'Enter') event.currentTarget.blur();
      }} />;
  };
  const historyButton = (group, row, scope) => {
    const entries = scope === 'price' ? row.priceHistory : row.rangeHistory;
    const enabled = entries?.length > 1;
    return <button type="button" disabled={!enabled || busy}
      className={`h-8 rounded border px-2 text-xs font-semibold ${enabled ? 'border-purple-600 bg-purple-600 text-white' : 'border-gray-200 bg-gray-50 text-gray-300'}`}
      onClick={() => { if (busyRef.current) return; setFocus({ groupId: group.id, bandId: row.id, scope,
        label: group.parameter ? group.title : `${group.title} · ${scope === 'price' ? 'Importo fascia' : 'Fascia'} ${row.min}–${row.max ?? '∞'}` }); }}>
      {group.parameter ? 'Storico valore' : scope === 'price' ? 'Importi' : 'Fasce'}
    </button>;
  };
  const table = (group) => {
    const rows = draft?.group.id === group.id ? draft.rows : group.rows;
    return <div key={group.id} className="min-w-0 rounded border border-purple-200 bg-white p-2">
      <div className="mb-1 text-sm font-semibold text-purple-900">{group.title}</div>
      <div className="max-h-[360px] overflow-auto"><table className="w-full text-xs"><thead className="sticky top-0 bg-purple-50"><tr>
        <th className="p-1 text-left">Da</th><th className="p-1 text-left">A</th><th className="p-1 text-left">{[1415,1420, 458].includes(group.code) ? 'Euro' : [427, 462].includes(group.code) ? '€ / cedolino' : '€ / mese attivo'}</th>
      </tr></thead><tbody>{rows.map((row, index) => <tr key={row.id} className="border-t">
        <td className="p-0.5">{input(group, row, 'min', index === 0)}</td>
        <td className="p-0.5"><div className="flex items-center gap-1">{index === rows.length - 1 && group.end == null
          ? <div aria-label={`${group.code} ${group.title} fascia ${row.id} max`} className="flex h-8 w-24 items-center justify-end rounded border border-gray-200 bg-gray-50 px-1 text-gray-500">∞</div>
          : input(group, row, 'max', index === rows.length - 1)}{historyButton(group, row, 'range')}</div></td>
        <td className="p-0.5"><div className="flex items-center gap-1">{input(group, row, 'price')}{historyButton(group, row, 'price')}</div></td>
      </tr>)}</tbody></table></div>
    </div>;
  };
  const focusedGroup = data?.groups.find((group) => group.id === focus?.groupId);
  const focusedRow = focusedGroup?.rows.find((row) => row.id === focus?.bandId);
  const entries = focus?.scope === 'price' ? focusedRow?.priceHistory : focusedRow?.rangeHistory;
  const sectionForCode = (code) => [458, 1415, 1420, 1421, 1422].includes(code) ? 6 : 0;
  const codes = [...new Set([1421, ...(data?.groups || []).map((group) => group.code)])].sort((a, b) => a - b);
  const sections = [...new Set(codes.map(sectionForCode))].sort((a, b) => a - b);
  return <div className="space-y-4" aria-busy={loading || busy}>
    {error && !focus && <div role="alert" className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">{error} <button className="underline" disabled={busy} onClick={() => { setError(''); load(); }}>Ricarica</button></div>}
    {loading && !data && <div>Caricamento listino…</div>}
    {data && <>
      {sections.map((section) => <section key={section} className="space-y-4">
        <h3 className="border-b pb-2 font-semibold">Sezione {section}</h3>
        {codes.filter((code) => sectionForCode(code) === section).map((code) => code === 1421
        ? <FasceCu1421 key={code} date={date} canEdit={canEdit} />
        : <fieldset disabled={busy || loading} key={code} className="rounded-lg border bg-gray-50 p-4">
          {DESCRIPTIONS[code] && <div className="mb-1 font-semibold">{code} · {DESCRIPTIONS[code]}</div>}
          {NOTES[code] && <div className="mb-3 text-xs text-gray-600">{NOTES[code]}</div>}
          <div className={`grid gap-4 ${[427, 456].includes(code) ? 'xl:grid-cols-2' : ''}`}>
            {data.groups.filter((group) => group.code === code && !group.parameter).map(table)}
            {data.groups.filter((group) => group.code === code && group.parameter).map((group) => <div key={group.id} className="rounded border bg-white p-3">
              <div className="mb-2 text-sm font-semibold">{group.code} · {group.title} (€)</div>
              {group.rows.map((row) => <div key={row.id} className="flex gap-2">{input(group, row, 'price')}{historyButton(group, row, 'price')}</div>)}
            </div>)}
          </div>
        </fieldset>)}
      </section>)}
    </>}
    {focus && <Modal isOpen size="tiny" closeButton={() => setFocus(null)} headerText={`Storico · ${focus.label}`}>
      {error && <div role="alert" className="mb-2 rounded bg-red-50 p-2 text-sm text-red-800">{error}</div>}
      <div className="max-h-[66vh] space-y-2 overflow-auto">
        {(entries || []).map((entry, index) => <div key={entry.eventId || `initial-${index}`} className="rounded border border-purple-200 p-2">
          <div className="mb-2 flex items-center justify-between gap-2"><span className="font-semibold text-purple-900">{dateLabel(entry.date)}</span>
            <span className="text-xs text-gray-500">{entry.initial ? 'Valore iniziale' : entry.legacy ? 'Storico precedente · autore non verificabile' : `da ${entry.author}`}</span>
            {canEdit && entry.deletable && <button disabled={busy} className="rounded border border-red-200 px-2 py-1 text-xs text-red-700" onClick={() => remove(entry)}>Elimina</button>}
          </div>
          {entry.recordedAt && <div className="mb-1 text-xs text-gray-500">Registrata il {new Date(entry.recordedAt).toLocaleString('it-IT', { timeZone: 'Europe/Rome' })}</div>}
          <div className="rounded bg-purple-50 px-2 py-1 text-right font-semibold text-purple-950">{Array.isArray(entry.value) ? `${entry.value[0]}–${entry.value[1] ?? '∞'}` : `${amount(entry.value)} €`}</div>
          {entry.coupled && <div className="mt-1 text-xs text-gray-500">Comprende i confini adiacenti ricalcolati nella stessa operazione.</div>}
        </div>)}
        {!entries?.length && <div>Nessuna variazione.</div>}
      </div>
    </Modal>}
  </div>;
}
